import { Component, computed, OnInit, signal } from '@angular/core';
import { ToastService } from '@app/core/services/toast.service';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LoginResponse } from 'api/models';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { UntilDestroy } from '@ngneat/until-destroy';
import { UsersService } from 'api/services';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-change-username',
    templateUrl: './change-username.component.html',
    styleUrls: ['./change-username.component.scss'],
    imports: [
        MatFormField,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        FormsModule,
        MatButton,
        TranslateModule,
    ],
})
export class ChangeUsernameComponent implements OnInit {
    userSession = signal<LoginResponse | undefined>(undefined);

    fullName = signal('');
    user: LoginResponse;
    loading = signal(false);

    usernameHasChanges = computed(() => {
        const session = this.userSession();
        return session ? session.fullName !== this.fullName() : false;
    });

    constructor(
        private sessionService: SessionService,
        private usersService: UsersService,
        private toastr: ToastService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        const session = this.sessionService.getSession();
        this.userSession.set(session);
        if (session) {
            this.fullName.set(session.fullName);
            this.user = session;
        }
    }

    async saveChanges() {
        this.loading.set(true);
        try {
            const user = await lastValueFrom(
                this.usersService.patchBasicUser({
                    body: {
                        fullName: this.fullName()?.trim(),
                    },
                })
            );
            this.userSession.update(s => s ? { ...s, fullName: user.fullName?.trim() } : s);
            await this.sessionService.loadUserFromApi();
            this.toastr.success(
                this.translateService.instant('SETTINGS.PROFILE.CHANGED')
            );
        } catch (error) {
            this.toastr.error(this.translateService.instant('GENERAL-ERROR'));
        } finally {
            // Timeout for better UX
            setTimeout(() => {
                this.loading.set(false);
            }, 300);
        }
    }
}
